/**
 * Surety's Motion for Remittitur of Bond Forfeiture (Lloyd, Sep 3 2026).
 *
 * The money half of the motion to withdraw as surety. The withdrawal asks the court to let the
 * surety surrender the defendant; the remittitur asks that the forfeited bond be handed back, less
 * costs, once that surrender is made.
 *
 * **There is deliberately no call here that produces the remittitur on its own.** The document's own
 * paragraph 2 tells the court the withdrawal motion was filed "contemporaneously", so the endpoint
 * returns both PDFs or refuses with the reasons. A remittitur alone is a claim to money with no
 * surrender behind it.
 *
 * The grounds are the withdrawal motion's, imported rather than restated, because paragraph 2 has to
 * recite the ground the companion motion is actually filed on.
 */

import {
  GenerateWithdrawalInput,
  WithdrawalBlockerDto,
  WithdrawalReadinessDto,
} from './SuretyWithdrawalApi';

export interface RemittiturReadinessResponseDto {
  readiness: WithdrawalReadinessDto;
  liveSuretyBonds: Array<{ guid?: string; type: string }>;
}

export interface GenerateRemittiturPacketInput extends GenerateWithdrawalInput {
  /**
   * The bond's face amount in whole cents. Defaults server-side to the amount on the bond, and is
   * sent only where staff have corrected it, so the usual path takes the recorded figure.
   */
  bondAmountCents?: number | null;
}

export interface RemittiturPacketDto {
  withdrawal: { fileName: string; pdfBase64: string };
  remittitur: { fileName: string; pdfBase64: string };
  bondGuid: string | null;
}

/** Thrown when the case cannot support an honest packet. Carries the reasons so the caller can list them. */
export class RemittiturBlockedError extends Error {
  constructor(
    message: string,
    public readonly blockers: WithdrawalBlockerDto[],
  ) {
    super(message);
    this.name = 'RemittiturBlockedError';
  }
}

export default class RemittiturApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  static async readiness(
    ticketGuid: string,
    params: { ground?: string; bondGuid?: string } = {},
  ): Promise<RemittiturReadinessResponseDto> {
    const query = new URLSearchParams();
    if (params.ground) query.set('ground', params.ground);
    // Without it a case carrying two live bonds previews blockers for a bond nobody picked.
    if (params.bondGuid) query.set('bondGuid', params.bondGuid);
    const queryString = query.toString();
    const response = await fetch(
      `${RemittiturApi.host()}/api/v1/manager/ticket/${ticketGuid}/remittitur-packet/readiness${
        queryString ? `?${queryString}` : ''
      }`,
      { credentials: 'include' },
    );
    if (!response.ok) {
      throw new Error('Failed to check whether this case is ready for a remittitur packet');
    }
    return response.json();
  }

  /** Both motions, or neither. */
  static async generatePacket(
    ticketGuid: string,
    input: GenerateRemittiturPacketInput,
  ): Promise<RemittiturPacketDto> {
    const response = await fetch(
      `${RemittiturApi.host()}/api/v1/manager/ticket/${ticketGuid}/remittitur-packet`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );

    // 400 here is the "which bond?" answer, not a validation slip, so it carries the same shape.
    if (response.status === 422 || response.status === 400) {
      const err = await response.json().catch(() => ({}));
      throw new RemittiturBlockedError(
        err.message || 'This case is not ready for a remittitur packet.',
        Array.isArray(err.blockers) ? err.blockers : [],
      );
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err.details || err.error || err.message || 'Failed to generate the remittitur packet',
      );
    }
    return response.json();
  }
}
